/**
 * Transport Logs — Admin Approval Queue (Phase 2)
 *
 * Reads:
 *   GET  /api/transport/logs?status=…&staff_id=&participant_id=
 *   GET  /api/transport/summary?period_start=&period_end=
 * Writes:
 *   POST /api/transport/logs/{id}/approve
 *   POST /api/transport/logs/{id}/reject
 *
 * Phase 1 invariant respected — approve/reject is a pure state
 * transition. No invoice/payroll/funding side effects.
 */
import React, { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CheckCircle, XCircle, Eye, Loader2, AlertTriangle, MapPinned } from 'lucide-react';
import { toast } from 'sonner';
import AsyncSearchSelect from '@/components/common/AsyncSearchSelect';
import { staffLabel, STAFF_SEARCH_HINT } from '@/components/common/pickerLabels';

const STATUS_COLORS = {
  DRAFT: 'bg-gray-100 text-gray-700',
  SUBMITTED: 'bg-blue-100 text-blue-700',
  AUTO_APPROVED: 'bg-emerald-100 text-emerald-700',
  APPROVED: 'bg-green-100 text-green-700',
  REJECTED: 'bg-red-100 text-red-700',
};

function StatusBadge({ status }) {
  return <Badge className={`${STATUS_COLORS[status] || 'bg-gray-100 text-gray-700'} text-xs`} data-testid={`status-${status}`}>{status}</Badge>;
}

export default function TransportLogsAdminPage() {
  const [status, setStatus] = useState('SUBMITTED');
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [featureDisabled, setFeatureDisabled] = useState(false);
  const [detail, setDetail] = useState(null);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [staffFilter, setStaffFilter] = useState('');
  const [branchFilter, setBranchFilter] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [staffOptions, setStaffOptions] = useState([]);
  const [branchOptions, setBranchOptions] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);

  useEffect(() => {
    (async () => {
      const [u, b, s] = await Promise.all([
        api.get('/admin/users').catch(() => ({ data: { data: [] } })),
        api.get('/org/branches').catch(() => ({ data: { data: [] } })),
        api.get('/org/states').catch(() => ({ data: { data: [] } })),
      ]);
      const users = u?.data?.data?.items || u?.data?.data || u?.data || [];
      setStaffOptions(users.map(x => ({ id: x.id || x._id, name: x.name || x.full_name || x.email })).filter(x => x.id));
      setBranchOptions((b?.data?.data || b?.data?.items || b?.data || []).map(x => ({ id: x.id || x._id || x.code, name: x.name || x.branch_name || x.code })).filter(x => x.id));
      setStateOptions((s?.data?.data || s?.data?.items || s?.data || []).map(x => ({ id: x.id || x._id || x.code, name: x.name || x.state_name || x.code })).filter(x => x.id));
    })();
  }, []);

  const staffNameById = useCallback((id) => {
    if (!id) return '—';
    const s = staffOptions.find(x => x.id === id);
    return s ? s.name : (String(id).slice(0, 8) + '…');
  }, [staffOptions]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { status };
      if (staffFilter) params.staff_id = staffFilter;
      if (branchFilter) params.branch_id = branchFilter;
      if (stateFilter) params.state_id = stateFilter;
      const r = await api.get('/transport/logs', { params }).catch(e => e.response);
      if (r?.status === 403 && r?.data?.error?.code === 'FEATURE_DISABLED') {
        setFeatureDisabled(true);
      } else {
        setLogs(r?.data?.data || []);
        setFeatureDisabled(false);
      }
      const s = await api.get('/transport/summary').catch(e => e.response);
      if (s?.data?.data) setSummary(s.data.data);
    } finally {
      setLoading(false);
    }
  }, [status, staffFilter, branchFilter, stateFilter]);

  useEffect(() => { load(); }, [load]);

  const doApprove = async (log) => {
    setBusyId(log.id || log._id);
    try {
      await api.post(`/transport/logs/${log.id || log._id}/approve`, { notes: '' });
      toast.success('Trip approved');
      setDetail(null);
      await load();
    } catch (err) {
      toast.error('Approve failed', { description: err?.response?.data?.error?.message });
    } finally { setBusyId(null); }
  };

  const doReject = async () => {
    if (!detail || !rejectReason || rejectReason.length < 3) return;
    setBusyId(detail.id || detail._id);
    try {
      await api.post(`/transport/logs/${detail.id || detail._id}/reject`, { reason: rejectReason });
      toast.success('Trip rejected');
      setRejectOpen(false);
      setRejectReason('');
      setDetail(null);
      await load();
    } catch (err) {
      toast.error('Reject failed', { description: err?.response?.data?.error?.message });
    } finally { setBusyId(null); }
  };

  if (featureDisabled) {
    return (
      <div className="container mx-auto p-6 space-y-4">
        <h1 className="text-2xl font-semibold">Transport · Trip Logs</h1>
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>Vehicle Log module is disabled. Enable <code>transport_logging_enabled</code> in Feature Flags.</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-4" data-testid="transport-logs-admin-page">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Transport · Trip Logs</h1>
          <p className="text-sm text-muted-foreground">Review and approve staff trip logs for NDIS Activity-Based Transport and personal-vehicle reimbursement.</p>
        </div>
      </div>

      {summary && (
        <div className="grid grid-cols-4 gap-3" data-testid="transport-summary-strip">
          <Card><CardContent className="pt-4"><div className="text-xs text-muted-foreground">Approved trips</div><div className="text-2xl font-semibold">{summary.trip_count}</div></CardContent></Card>
          <Card><CardContent className="pt-4"><div className="text-xs text-muted-foreground">Total km</div><div className="text-2xl font-semibold">{summary.total_km}</div></CardContent></Card>
          <Card><CardContent className="pt-4"><div className="text-xs text-muted-foreground">NDIS $ (preview)</div><div className="text-2xl font-semibold">${summary.ndis_amount_preview_total}</div></CardContent></Card>
          <Card><CardContent className="pt-4"><div className="text-xs text-muted-foreground">Reimb $ (preview)</div><div className="text-2xl font-semibold">${summary.staff_reimbursement_preview_total}</div></CardContent></Card>
        </div>
      )}

      <div className="flex flex-wrap items-end gap-3" data-testid="trip-logs-filters">
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground block">Staff</label>
          <div className="w-[220px]">
            <AsyncSearchSelect
              endpoint="/staff"
              value={staffFilter}
              onChange={v => setStaffFilter(v || '')}
              getOptionValue={(s) => s.user_id || s.id}
              getOptionLabel={staffLabel}
              selectedLabel={staffFilter ? staffNameById(staffFilter) : ''}
              placeholder="All staff"
              searchPlaceholder={STAFF_SEARCH_HINT}
              emptyText="No staff match that search"
              clearLabel="All staff"
              testId="trip-filter-staff"
            />
          </div>
        </div>
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground block">Branch</label>
          <Select value={branchFilter || '_all'} onValueChange={v => setBranchFilter(v === '_all' ? '' : v)}>
            <SelectTrigger className="h-9 w-[160px] text-sm" data-testid="trip-filter-branch"><SelectValue placeholder="All branches" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">All branches</SelectItem>
              {branchOptions.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground block">State</label>
          <Select value={stateFilter || '_all'} onValueChange={v => setStateFilter(v === '_all' ? '' : v)}>
            <SelectTrigger className="h-9 w-[160px] text-sm" data-testid="trip-filter-state"><SelectValue placeholder="All states" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">All states</SelectItem>
              {stateOptions.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {(staffFilter || branchFilter || stateFilter) && (
          <Button variant="ghost" size="sm" className="h-9 text-xs text-muted-foreground" onClick={() => { setStaffFilter(''); setBranchFilter(''); setStateFilter(''); }} data-testid="trip-filter-clear">Clear filters</Button>
        )}
      </div>

      <Tabs value={status} onValueChange={setStatus}>
        <TabsList>
          <TabsTrigger value="SUBMITTED" data-testid="tab-submitted">Submitted</TabsTrigger>
          <TabsTrigger value="APPROVED" data-testid="tab-approved">Approved</TabsTrigger>
          <TabsTrigger value="AUTO_APPROVED" data-testid="tab-auto-approved">Auto-Approved</TabsTrigger>
          <TabsTrigger value="REJECTED" data-testid="tab-rejected">Rejected</TabsTrigger>
        </TabsList>
        <TabsContent value={status}>
          <Card>
            <CardContent className="pt-4">
              {loading ? (
                <div className="flex items-center justify-center py-12 text-muted-foreground"><Loader2 className="h-4 w-4 mr-2 animate-spin" />Loading…</div>
              ) : logs.length === 0 ? (
                <div className="text-center text-muted-foreground py-12 text-sm" data-testid={`no-${status.toLowerCase()}-trips`}>No {status.toLowerCase()} trips.</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Staff</TableHead>
                      <TableHead>Vehicle</TableHead>
                      <TableHead className="text-right">Km</TableHead>
                      <TableHead className="text-right">NDIS preview $</TableHead>
                      <TableHead className="text-right">Reimb preview $</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {logs.map(l => (
                      <TableRow key={l.id || l._id} data-testid={`trip-row-${l.id || l._id}`}>
                        <TableCell className="text-xs">{(l.start_time || l.created_at || '').slice(0, 10)}</TableCell>
                        <TableCell className="text-xs">{l.staff_name || staffNameById(l.staff_id)}</TableCell>
                        <TableCell className="text-xs capitalize">{l.vehicle_kind}</TableCell>
                        <TableCell className="text-right">{l.distance_km}</TableCell>
                        <TableCell className="text-right">${l.ndis_amount_preview ?? 0}</TableCell>
                        <TableCell className="text-right">${l.staff_reimbursement_amount_preview ?? 0}</TableCell>
                        <TableCell><StatusBadge status={l.status} /></TableCell>
                        <TableCell className="text-right">
                          <Button size="sm" variant="ghost" onClick={() => setDetail(l)} data-testid={`trip-view-${l.id || l._id}`}><Eye className="h-3 w-3" /></Button>
                          {(l.status === 'SUBMITTED' || l.status === 'DRAFT') && (
                            <Button size="sm" variant="default" disabled={busyId === (l.id || l._id)} onClick={() => doApprove(l)} data-testid={`trip-approve-${l.id || l._id}`}>
                              {busyId === (l.id || l._id) ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle className="h-3 w-3" />}
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Trip log detail</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-2 text-sm">
              <div><MapPinned className="h-3 w-3 inline mr-1" /><strong>Purpose:</strong> {detail.purpose}</div>
              <div><strong>Distance:</strong> {detail.distance_km} km {detail.start_odometer != null && `(${detail.start_odometer} → ${detail.end_odometer})`}</div>
              <div><strong>Vehicle kind:</strong> {detail.vehicle_kind}</div>
              <div><strong>NDIS preview:</strong> ${detail.ndis_amount_preview ?? 0} @ ${detail.ndis_rate_per_km ?? '—'}/km {detail.ndis_billable ? '(billable)' : '(non-billable)'}</div>
              <div><strong>Reimb preview:</strong> ${detail.staff_reimbursement_amount_preview ?? 0} @ ${detail.staff_reimbursement_rate_per_km ?? '—'}/km</div>
              <div><strong>Status:</strong> <StatusBadge status={detail.status} /></div>
              {detail.notes && <div><strong>Notes:</strong> {detail.notes}</div>}
              {detail.rejection_reason && <div className="text-red-600"><strong>Rejected reason:</strong> {detail.rejection_reason}</div>}
              <p className="text-xs text-muted-foreground pt-2 border-t">Preview amounts only — Phase 1 build does NOT emit invoice or payroll lines. Wait until Phase 4/5 sub-flags are ON.</p>
            </div>
          )}
          <DialogFooter>
            {detail && (detail.status === 'SUBMITTED' || detail.status === 'DRAFT') && (
              <>
                <Button variant="outline" onClick={() => setRejectOpen(true)} data-testid="trip-reject-open"><XCircle className="h-3 w-3 mr-1" />Reject</Button>
                <Button onClick={() => doApprove(detail)} disabled={busyId === (detail.id || detail._id)} data-testid="trip-approve-detail"><CheckCircle className="h-3 w-3 mr-1" />Approve</Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reject trip log</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <label className="text-sm">Reason (minimum 3 characters)</label>
            <Textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)} placeholder="Wrong vehicle, off-shift trip, missing photo, etc." data-testid="reject-reason-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={doReject} disabled={rejectReason.length < 3 || busyId} data-testid="reject-confirm">Reject</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
